import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Container, Paragraph } from '@scentregroup/ui'
import { parseHostname } from '../../../helpers/url'
import movieCardsModule from '../../../helpers/movie-cards-module'
import whatsHappeningModule from '../../../helpers/whats-happening-module'
import withCinema from '../../../containers/with-cinema'
import PageLanding from '../../../components/page-landing'
import RetailerUtilityBox from '../../../components/retailer-utility-box'
import RetailerFeature from '../../../components/retailer-feature'
import pageLandingStyle from '../../../components/page-landing/index.module.scss'
import Markdown from '../../../components/markdown'
import ShowMoreText from '../../../components/show-more-text'
import pagesHoc from '../../pages-hoc'
import withModules from '../../../containers/with-modules'
import Modules from '../../../components/modules'
import pageLoadDataLayer, {
  mapMovieExperienceToPageLoadData,
} from '../../../helpers/analytics-utils/page-load-data-layer'
import { useEvolvedContext } from '../../../context'
import style from './index.module.scss'

const paragraphRenderer = ({ children }) => (
  <Paragraph className={style.articleParagraph}>{children}</Paragraph>
)

const CinemaDetail = ({
  movies,
  cinema,
  centre,
  desktopBackgroundImage,
  mobileBackgroundImage,
  nextWeek,
  logo,
  modules,
  happenings,
}) => {
  const isEvolved = useEvolvedContext()
  useEffect(() => {
    pageLoadDataLayer(mapMovieExperienceToPageLoadData(cinema), centre)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { slug, legacyIdentifier } = cinema
  const locationLink = `/${centre.slug}/centre-map/${legacyIdentifier}/${slug}/`
  const store = {
    heading: 'Visit us',
    phoneNumber: cinema.telephoneNumber,
    link: cinema.websiteAddress,
    linkText: parseHostname(cinema.websiteAddress),
    location: cinema.physicalAddress,
    nearestLocation: cinema.landmarkDescription,
    locationLink,
    storeImage: (cinema.storeFrontImage || {}).url,
    status: cinema.status || 'Open',
    openHours: cinema.openHours,
  }
  const title = cinema.title || (cinema.retailer && cinema.retailer.title)
  const singleColumnQuery = `(max-width: ${pageLandingStyle['single-column-max-width']})`
  const whatsHappeningEntries = happenings.filter(entry =>
    entry.storeIds.includes(cinema.sys.id)
  )

  return (
    <Container>
      <PageLanding
        logo={logo.src}
        heading="Movies"
        desktopImage={{
          src: desktopBackgroundImage.src,
          alt: desktopBackgroundImage.title,
        }}
        mobileImage={{
          src: mobileBackgroundImage.src,
          alt: mobileBackgroundImage.title,
        }}
      >
        <RetailerFeature
          className={pageLandingStyle.contentWrapper}
          title={title}
          body={
            <ShowMoreText minLines={3} matchMedia={singleColumnQuery}>
              <Markdown
                content={
                  cinema.longDescription ||
                  (cinema.retailer && cinema.retailer.longDescription)
                }
                customRenderers={
                  isEvolved
                    ? () => ({
                        paragraph: paragraphRenderer,
                      })
                    : undefined
                }
              />
            </ShowMoreText>
          }
        />
        <div className={pageLandingStyle.sideBar}>
          <RetailerUtilityBox heading="Visit us" {...store} />
        </div>
      </PageLanding>
      <Modules
        modules={[
          movieCardsModule(centre, 'Now showing', movies, nextWeek),
          ...(whatsHappeningEntries.length
            ? [whatsHappeningModule(whatsHappeningEntries, title, centre)]
            : []),
          ...modules,
        ]}
      />
    </Container>
  )
}

CinemaDetail.propTypes = {
  movies: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      genre: PropTypes.string,
      classification: PropTypes.string,
      sessions: PropTypes.array,
    })
  ),
  cinema: PropTypes.shape({
    telephoneNumber: PropTypes.string,
    websiteAddress: PropTypes.string,
    physicalAddress: PropTypes.string,
    waypoint: PropTypes.string,
    title: PropTypes.string,
    slug: PropTypes.string,
    legacyIdentifier: PropTypes.string,
    landmarkDescription: PropTypes.string,
    longDescription: PropTypes.string,
    retailer: PropTypes.object,
    storeFrontImage: PropTypes.any,
    status: PropTypes.string,
    sys: PropTypes.object,
    openHours: PropTypes.any,
  }),
  centre: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  desktopBackgroundImage: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
    src: PropTypes.string,
  }),
  mobileBackgroundImage: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
    src: PropTypes.string,
  }),
  match: PropTypes.object,
  nextWeek: PropTypes.array.isRequired,
  templatePath: PropTypes.string,
  logo: PropTypes.object,
  modules: PropTypes.array,
  happenings: PropTypes.array,
}

CinemaDetail.defaultProps = {
  happenings: [],
  modules: [],
}

const getMetaData = ({ centre, cinema }) => ({
  title: `Movie Times at Westfield ${centre.title}`,
  description: cinema.longDescription,
  schema: {},
})

export default withCinema(withModules(pagesHoc(CinemaDetail, getMetaData)))
